import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaSearch, FaFileDownload, FaSpinner, FaCalendar, FaClock,
  FaEye, FaUser, FaMoneyBillWave, FaIdCard, FaCheckCircle,
  FaTimesCircle, FaArrowLeft, FaArrowRight, FaFilter, FaSync,
  FaExclamationTriangle, FaInfoCircle, FaTag, FaBuilding,
  FaUsers, FaChartBar, FaCreditCard, FaDollarSign, FaWallet
} from 'react-icons/fa';

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showTodayOnly, setShowTodayOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Wallet details state
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletDetails, setWalletDetails] = useState(null);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  // Function to filter data between start and end dates
  const filterDataByDateRange = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }

    console.log(`Filtering data between ${startDate} and ${endDate}`);

    // Validate date range
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      alert("Start date cannot be after end date");
      return;
    }
  };

  // Reset date filters
  const clearDateFilters = () => {
    setStartDate("");
    setEndDate("");
    setShowTodayOnly(false);
  };

  // Handle Today checkbox
  const handleTodayCheckbox = (e) => {
    const isChecked = e.target.checked;
    setShowTodayOnly(isChecked);

    if (isChecked) {
      // Set today's date for both start and end using local timezone
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`; // Format: YYYY-MM-DD
      setStartDate(formattedDate);
      setEndDate(formattedDate);
    } else {
      // Clear the dates if unchecked
      setStartDate("");
      setEndDate("");
    }
  };

  // Fetch payment history on component mount
  useEffect(() => {
    const fetchPaymentHistory = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/revenew`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Payment history response:", response);
        // The API response structure is: response.data.data.payments
        // Payments are already sorted by createdAt: -1 (latest first) from backend
        const paymentsData = Array.isArray(response.data.data?.payments) ? response.data.data.payments : [];
        // Sort by date descending (latest first) - ensure proper sorting
        const sortedPayments = [...paymentsData].sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || 0);
          const dateB = new Date(b.date || b.createdAt || 0);
          return dateB - dateA; // Latest first
        });
        setPayments(sortedPayments);
      } catch (error) {
        setError("Error fetching payment history.");
      } finally {
        setLoading(false);
      }
    };
    fetchPaymentHistory();
  }, []);

  // Filter payments by ID, user name, amount, or date
  const filteredPayments = payments.filter((payment) => {
    const paymentId = payment.paymentId?.toLowerCase() || "";
    const userName = payment.user?.name?.toLowerCase() || "";
    const userEmail = payment.user?.email?.toLowerCase() || "";
    const amount = payment.amount?.toString() || "";
    const method = payment.method?.toLowerCase() || "";
    const status = payment.status?.toLowerCase() || "";
    const date = payment.date
      ? new Date(payment.date).toLocaleDateString().toLowerCase()
      : "";
    const search = searchTerm.toLowerCase();

    // Apply text search filter
    const matchesSearch = !searchTerm ||
      paymentId.includes(search) ||
      userName.includes(search) ||
      userEmail.includes(search) ||
      amount.includes(search) ||
      method.includes(search) ||
      status.includes(search) ||
      date.includes(search);

    // Apply date range filter
    const paymentDate = payment.date ? new Date(payment.date) : null;

    // Create new date objects to avoid mutating the original dates
    const filterStartDate = startDate ? new Date(startDate + 'T00:00:00') : null;
    const filterEndDate = endDate ? new Date(endDate + 'T23:59:59.999') : null;

    const withinDateRange =
      (!filterStartDate || (paymentDate && paymentDate >= filterStartDate)) &&
      (!filterEndDate || (paymentDate && paymentDate <= filterEndDate));

    return matchesSearch && withinDateRange;
  });

  // Pagination calculations
  // Sort filtered payments by date descending (latest first)
  const sortedFilteredPayments = [...filteredPayments].sort((a, b) => {
    const dateA = new Date(a.date || a.createdAt || 0);
    const dateB = new Date(b.date || b.createdAt || 0);
    return dateB - dateA; // Latest first
  });

  const totalPages = Math.ceil(sortedFilteredPayments.length / rowsPerPage);
  const paginatedPayments = sortedFilteredPayments.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Change page
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Fetch wallet details by payment ID
  const handleViewWalletDetails = async (paymentId) => {
    setLoadingWallet(true);
    setSelectedPaymentId(paymentId);
    setShowWalletModal(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/wallet/by-payment-id/${paymentId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        setWalletDetails(response.data.data);
      } else {
        setError("Failed to fetch wallet details");
        setShowWalletModal(false);
      }
    } catch (error) {
      console.error("Error fetching wallet details:", error);
      setError("Failed to fetch wallet details");
      setShowWalletModal(false);
    } finally {
      setLoadingWallet(false);
    }
  };

  const closeWalletModal = () => {
    setShowWalletModal(false);
    setWalletDetails(null);
    setSelectedPaymentId(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-4 sm:p-6">
      {/* Header Section */}
      <div className="bg-white rounded-2xl shadow-xl mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                <FaCreditCard className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Payment History</h1>
                <p className="text-green-100">Track all payment transactions and their status</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-white">
                {filteredPayments.length}
              </div>
              <div className="text-green-100">Total Payments</div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="p-6 bg-gray-50">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by Payment ID, User Name, Amount, Method..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Date Range Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center space-x-2">
                <FaCalendar className="text-green-500 w-4 h-4" />
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setShowTodayOnly(false);
                  }}
                  max={endDate || undefined}
                  disabled={showTodayOnly}
                />
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-gray-400">to</span>
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setShowTodayOnly(false);
                  }}
                  min={startDate || undefined}
                  disabled={showTodayOnly}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="today-checkbox"
                  type="checkbox"
                  className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                  checked={showTodayOnly}
                  onChange={handleTodayCheckbox}
                />
                <label htmlFor="today-checkbox" className="text-sm font-medium text-gray-700">
                  Today Only
                </label>
              </div>

              <button
                onClick={filterDataByDateRange}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
                disabled={!startDate || !endDate}
              >
                <FaSearch className="w-4 h-4" />
                <span>Filter</span>
              </button>

              {(startDate || endDate) && (
                <button
                  onClick={clearDateFilters}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Filter Status */}
          {startDate && endDate && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <FaInfoCircle className="text-green-500 w-4 h-4" />
                <span className="text-green-700 font-medium">
                  {showTodayOnly
                    ? "Showing today's payments only"
                    : `Showing payments between ${new Date(startDate).toLocaleDateString()} and ${new Date(endDate).toLocaleDateString()}`
                  }
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Payments</p>
              <p className="text-2xl font-bold text-green-600">{filteredPayments.length}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <FaCreditCard className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Amount</p>
              <p className="text-2xl font-bold text-blue-600">
                ₹{filteredPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0).toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FaDollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total GST</p>
              <p className="text-2xl font-bold text-purple-600">
                ₹{filteredPayments.reduce((sum, payment) => sum + (payment.gst || 0), 0).toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <FaTag className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-emerald-600">
                {filteredPayments.length > 0
                  ? Math.round((filteredPayments.filter(p => p.status === 'captured').length / filteredPayments.length) * 100)
                  : 0}%
              </p>
            </div>
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <FaChartBar className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-green-200 border-t-green-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <FaSpinner className="w-6 h-6 text-green-500 animate-pulse" />
              </div>
            </div>
            <p className="mt-4 text-gray-600 font-medium">Loading payment history...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <FaExclamationTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 flex items-center space-x-2"
            >
              <FaSync className="w-4 h-4" />
              <span>Try Again</span>
            </button>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FaCreditCard className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Payments Found</h3>
            <p className="text-gray-600 text-center">
              {searchTerm || startDate || endDate
                ? "No payments match your current filters. Try adjusting your search criteria."
                : "There are no payment records to display at this time."
              }
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-green-500 to-emerald-500">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaIdCard className="w-4 h-4" />
                        Payment ID
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaCalendar className="w-4 h-4" />
                        Date
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaDollarSign className="w-4 h-4" />
                        Amount
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaTag className="w-4 h-4" />
                        GST
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaCheckCircle className="w-4 h-4" />
                        Status
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaUser className="w-4 h-4" />
                        User
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaCreditCard className="w-4 h-4" />
                        Method
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaWallet className="w-4 h-4" />
                        Wallet
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedPayments.map((payment) => (
                    <tr key={payment.paymentId} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {payment.paymentId?.charAt(0) || 'P'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 font-mono">
                              {payment.paymentId || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <FaClock className="w-4 h-4 text-gray-400" />
                          <span>{new Date(payment.date).toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-lg font-bold text-green-600">
                          ₹{payment.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-purple-600">
                          ₹{payment.gst?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${payment.status === 'captured'
                            ? 'bg-green-100 text-green-800'
                            : payment.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                          {payment.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{payment.user?.name || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{payment.user?.email || 'N/A'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {payment.method || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewWalletDetails(payment.paymentId)}
                          className="px-3 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-sm font-medium transition-colors shadow-sm hover:shadow-md flex items-center gap-2"
                          title="View Wallet Details"
                        >
                          <FaWallet className="w-3 h-3" />
                          View Wallet
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden p-4 space-y-4">
              {paginatedPayments.map((payment) => (
                <div key={payment.paymentId} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold">
                        {payment.paymentId?.charAt(0) || 'P'}
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">{payment.user?.name || 'N/A'}</div>
                        <div className="text-sm text-gray-600 font-mono">{payment.paymentId || 'N/A'}</div>
                        <div className="text-xs text-gray-500">{payment.user?.email || 'N/A'}</div>
                      </div>
                    </div>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${payment.status === 'captured'
                        ? 'bg-green-100 text-green-800'
                        : payment.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                      {payment.status || 'Unknown'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-gray-600">Amount</div>
                      <div className="text-lg font-bold text-green-600">
                        ₹{payment.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">GST</div>
                      <div className="text-sm font-medium text-purple-600">
                        ₹{payment.gst?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Method</div>
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {payment.method || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Date</div>
                      <div className="text-sm font-medium text-gray-900">
                        {new Date(payment.date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={() => handleViewWalletDetails(payment.paymentId)}
                      className="w-full px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      <FaWallet className="w-4 h-4" />
                      View Wallet Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && !error && filteredPayments.length > 0 && totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * rowsPerPage + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * rowsPerPage, filteredPayments.length)}
                </span>{" "}
                of <span className="font-medium">{filteredPayments.length}</span> results
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-lg border transition-all duration-200 flex items-center space-x-1 ${currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-green-50 hover:border-green-300 border-gray-300"
                    }`}
                >
                  <FaArrowLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNumber = i + 1;
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${currentPage === pageNumber
                            ? "bg-green-500 text-white"
                            : "bg-white text-gray-700 hover:bg-green-50 border border-gray-300"
                          }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 rounded-lg border transition-all duration-200 flex items-center space-x-1 ${currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-green-50 hover:border-green-300 border-gray-300"
                    }`}
                >
                  <span>Next</span>
                  <FaArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Wallet Details Modal */}
      {showWalletModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative border border-gray-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-700 text-white p-6 rounded-t-2xl relative">
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold bg-white bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                onClick={closeWalletModal}
                title="Close"
              >
                &times;
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <FaWallet className="w-6 h-6 text-indigo-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Wallet Details</h3>
                  <p className="text-indigo-100 text-sm">
                    Payment ID: {selectedPaymentId}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8">
              {loadingWallet ? (
                <div className="flex justify-center items-center h-64">
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative w-16 h-16">
                      <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-indigo-500 animate-spin"></div>
                      <div className="absolute inset-2 rounded-full border-4 border-transparent border-r-purple-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                    </div>
                    <p className="text-gray-600 font-medium">Loading wallet details...</p>
                  </div>
                </div>
              ) : walletDetails ? (
                <>
                  {/* Wallet Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-green-500 text-white rounded-lg p-2">
                          <FaWallet className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Current Balance</h5>
                          <p className="text-sm text-gray-600">Wallet balance</p>
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-green-600">
                        ₹{walletDetails.wallet?.balance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-500 text-white rounded-lg p-2">
                          <FaUser className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">User Type</h5>
                          <p className="text-sm text-gray-600">Account type</p>
                        </div>
                      </div>
                      <div className="text-xl font-bold text-blue-600">
                        {walletDetails.wallet?.userType || 'N/A'}
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-500 text-white rounded-lg p-2">
                          <FaCreditCard className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Total Transactions</h5>
                          <p className="text-sm text-gray-600">All transactions</p>
                        </div>
                      </div>
                      <div className="text-xl font-bold text-purple-600">
                        {walletDetails.allTransactions?.length || 0}
                      </div>
                    </div>
                  </div>

                  {/* User Information */}
                  {walletDetails.user && (
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-2xl border border-indigo-200 mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-indigo-500 text-white rounded-lg p-2">
                          <FaUser className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">User Information</h5>
                          <p className="text-sm text-gray-600">Account holder details</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-3 border border-indigo-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Name</span>
                          <span className="font-medium text-gray-900">{walletDetails.user.name || 'N/A'}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-indigo-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Email</span>
                          <span className="font-medium text-gray-900">{walletDetails.user.email || 'N/A'}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-indigo-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Mobile</span>
                          <span className="font-medium text-gray-900">{walletDetails.user.mobile || 'N/A'}</span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-indigo-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">IINSAF ID</span>
                          <span className="font-medium text-gray-900">{walletDetails.user.iinsafId || 'N/A'}</span>
                        </div>
                        {walletDetails.user.organization && (
                          <div className="bg-white rounded-lg p-3 border border-indigo-200 md:col-span-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Organization</span>
                            <span className="font-medium text-gray-900">{walletDetails.user.organization}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Selected Transaction */}
                  {walletDetails.transaction && (
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-2xl border border-yellow-200 mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-yellow-500 text-white rounded-lg p-2">
                          <FaInfoCircle className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">Selected Transaction</h5>
                          <p className="text-sm text-gray-600">Transaction for payment ID: {selectedPaymentId}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Type</span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${walletDetails.transaction.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                            {walletDetails.transaction.type || 'N/A'}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Amount</span>
                          <span className={`text-lg font-bold ${walletDetails.transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                            }`}>
                            {walletDetails.transaction.type === 'credit' ? '+' : '-'}₹{walletDetails.transaction.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Status</span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${walletDetails.transaction.status === 'success' ? 'bg-green-100 text-green-800' :
                              walletDetails.transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                            }`}>
                            {walletDetails.transaction.status || 'N/A'}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-yellow-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Date</span>
                          <span className="font-medium text-gray-900">
                            {walletDetails.transaction.date ? new Date(walletDetails.transaction.date).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                        {walletDetails.transaction.description && (
                          <div className="bg-white rounded-lg p-3 border border-yellow-200 md:col-span-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Description</span>
                            <span className="font-medium text-gray-900">{walletDetails.transaction.description}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* All Transactions */}
                  {walletDetails.allTransactions && walletDetails.allTransactions.length > 0 && (
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-2xl border border-gray-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-gray-500 text-white rounded-lg p-2">
                          <FaCreditCard className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">All Transactions</h5>
                          <p className="text-sm text-gray-600">Complete transaction history</p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-lg">
                          <thead className="bg-gray-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {walletDetails.allTransactions.slice(0, 10).map((transaction, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {transaction.date ? new Date(transaction.date).toLocaleString() : 'N/A'}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${transaction.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}>
                                    {transaction.type || 'N/A'}
                                  </span>
                                </td>
                                <td className={`px-4 py-3 text-sm font-bold ${transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                  {transaction.type === 'credit' ? '+' : '-'}₹{transaction.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${transaction.status === 'success' ? 'bg-green-100 text-green-800' :
                                      transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                    }`}>
                                    {transaction.status || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {transaction.description || 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {walletDetails.allTransactions.length > 10 && (
                          <div className="mt-4 text-center text-sm text-gray-600">
                            Showing first 10 of {walletDetails.allTransactions.length} transactions
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Close Button */}
                  <div className="mt-8 flex justify-end">
                    <button
                      onClick={closeWalletModal}
                      className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl"
                    >
                      Close
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No wallet details available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;